/**
 * Wire shapes for /api/v2/admin/users + /api/v2/admin/patients (cycle-17).
 * BE applies the shared toSnake() interceptor so every leaf field arrives
 * in snake_case + role/subscribe hydrated.
 *
 * Per the P19 plan: keep User shapes loose ([key: string]: any) — the FE
 * touches a wide field surface per consumer and the legacy GQL fragment
 * has many optional fields.
 */

// Phase E Cycle 2 canonical (BE @app/database/core.constants
// DoctorProfileStatus). Was numeric '1'..'4'.
export enum DoctorProfileStatus {
  Waiting = 'waiting',
  Active = 'active',
  Disabled = 'disabled',
  Inactive = 'inactive'
}

// Phase E Cycle 5 canonical Gender (BE shared across patients +
// doctors + treatment_forms). Was numeric '1' / '2'.
export enum Gender {
  Male = 'male',
  Female = 'female'
}

// Phase E Cycle 2 canonical PatientSource. Was numeric '1'..'4'.
export enum PatientSource {
  SignUp = 'sign_up',
  FastAppointment = 'fast_appointment',
  Another = 'another',
  Other = 'other'
}

export interface UserRole {
  id?: number;
  name: string;
}

export interface UserListRow {
  id: number;
  first_name: string;
  last_name: string;
  avatar: string | null;
  phone: string | null;
  email: string;
  confirm: number | null;
  lang: string | null;
  created_at: string;
  /** Actor type — 'admin' for every row of /users?role=admin. Not the RBAC role. */
  role: UserRole | null;
  /**
   * The RBAC role the account holds — "Content Manager", "super_admin".
   * snake_case because /users goes through toSnake; /roles does not.
   * null when the account has no user_roles row.
   */
  role_name: string | null;
  role_id: number | null;
  /**
   * The role's display name — "Ceiling Test (temporary)" where role_name is
   * "ceiling_test". camelCase among the snake neighbours, like canAct: it is
   * not on toSnake's hand-maintained rename list. Null when the admin holds
   * no RBAC role.
   */
  roleLabel?: string | null;
  /**
   * Which organizations this admin belongs to. One grouped read on a page
   * that was already being fetched.
   *
   * `name` is the jsonb translation map, handed over as-is — every other
   * organization name in the panel picks its own language, and this one is
   * not the place to have that chosen for us. Read it the way the picker
   * does: `name?.en`.
   *
   * Empty array when they belong to none; ABSENT on the doctor, patient and
   * translator lists, where membership is its own surface. Absent is "not
   * applicable", not "none".
   */
  organizations?: Array<{ id: number; name: Record<string, string> }>;
  /**
   * Was this admin given ALL organizations, as a standing grant?
   *
   * NOT THE SAME AS `organizations` happening to hold every one today.
   * The flag means a superadmin pressed All, and the backend grants each
   * new organization to the admins holding it — so 19 of 19 stays 20 of 20
   * instead of silently becoming 19 of 20. Ticking every box by hand is a
   * different statement and sets nothing.
   *
   * Only ever set by a SUPERADMIN's All. For a delegated admin "all" means
   * "all of mine", a bounded set that is already a snapshot — making it
   * standing would let one admin widen another's access just by being added
   * to an organization themselves.
   *
   * CLEARED BY ANY DETACH, in the same transaction. An admin missing one is
   * a list, not "all".
   *
   * `organizations` stays complete either way; this is extra.
   */
  allOrganizations?: boolean;
  /**
   * May the caller change THIS admin's role? Server-computed, strictly
   * below: peers, incomparable pairs and the caller themselves are all
   * false, a superadmin is exempt and reads true on everyone.
   *
   * NOT THE SAME QUESTION AS canAct ON A ROLE ROW, which is a subset test
   * that a peer passes. Seeing is not acting either — the list already
   * decided who appears, and an operator with admins.read can see a whole
   * branch while being able to touch none of it.
   *
   * camelCase among snake_case neighbours, deliberately: /users goes
   * through toSnake() whose rename list is hand-maintained, and this key
   * is not on it. The backend pins that with a test; do not "fix" it.
   *
   * ABSENT, not false, on the doctor/patient/translator lists — they have
   * no permission map and nobody above them, so "not applicable" and
   * "refused" stay apart. Read it through adminActionRefusal().
   */
  canAct?: boolean;
  /** Why canAct is false, as a sentence fit to show. Null when it is true. */
  canActReason?: string | null;
  /**
   * May the caller DELETE this admin? Server-computed, and deliberately
   * not derivable from canAct — the two disagree on purpose.
   *
   * On a superadmin's own row canAct is true and canDelete is false, so a
   * Remove button wired to canAct would offer the operator their own
   * deletion. A superadmin ACCOUNT cannot be deleted by anyone at all,
   * including another superadmin: the panel must always have one.
   *
   * Absent on the non-admin lists, where it means "not applicable".
   */
  canDelete?: boolean;
  /** Why canDelete is false. Null when it is true. */
  canDeleteReason?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface UserListResponse {
  results: UserListRow[];
  total: number;
}

export interface UserListQuery {
  page?: number; // 0-based at FE; service coerces to 1-based BE
  limit?: number;
  role?: string;
  name?: string;
}

export interface PatientSubscribe {
  auto_renew_status: boolean | null;
  duration_type: number | null;
  expiration_date: string | null;
  subscription_id: number | null;
}

export interface PatientListRow extends UserListRow {
  subscribe: PatientSubscribe | null;
}

export interface PatientListResponse {
  results: PatientListRow[];
  total: number;
}

export interface PatientListQuery {
  page?: number;
  limit?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
  birthday_start?: string;
  birthday_end?: string;
  gender?: Gender;
  confirm?: number;
  lang?: string;
  subscription?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserInput = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CreateDoctorInput = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UpdateUserInput = Record<string, any>;

export interface DoctorStatusInput {
  status: DoctorProfileStatus;
  lang?: string;
}
